import React, { useState, useEffect } from 'react';
import MainLayout from '../components/Layout/MainLayout.js';
import CvrForm from '../components/OnboardingFlow/CvrForm.js';
import CompanyPreview from '../components/OnboardingFlow/CompanyPreview.js';
import { 
    fetchCompanyData, 
    checkExistingCVR, 
    createCompany,
    searchExistingWebsite
} from '../services/companyService.js';
import { updateMetaTags } from '../utils/metaTags.js';
import { trackEvent } from '../services/analytics.js';

const Onboarding = () => {
    useEffect(() => {
        updateMetaTags(
            'Opret hjemmeside - HåndværkerNet',
            'Opret hjemmeside på HåndværkerNet. AI-genereret indhold, billedegalleri, nemt kontrolpanel og meget mere.',
            'https://haandvaerkernet.dk/images/logo.png'
        );
    }, []);

    const [cvrNumber, setCvrNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [companyPreview, setCompanyPreview] = useState(null);

    const handleSubmit = async (formData) => {
        setLoading(true);
        setError(null);

        try {
            const exists = await checkExistingCVR(formData.cvr);
            if (exists) {
                trackEvent('cvr_lookup', {
                    cvr_number: formData.cvr,
                    status: 'already_registered'
                });
                throw new Error('Dette CVR nummer er allerede registreret');
            }

            // Fetch company data from CVR API
            const companyData = await fetchCompanyData(formData.cvr);
            
            // Search for existing website using company name
            const websiteSearch = await searchExistingWebsite(companyData.name);
            
            // Track successful company data fetch
            trackEvent('cvr_lookup', {
                cvr_number: formData.cvr,
                status: 'success',
                has_website: !!websiteSearch.found
            });

            setCompanyPreview({
                cvr: formData.cvr,
                name: companyData.name,
                address: companyData.address,
                city: companyData.city,
                zipcode: companyData.zipcode,
                existingWebsite: websiteSearch.found ? {
                    url: websiteSearch.url,
                    title: websiteSearch.title,
                    description: websiteSearch.description
                } : null
            });
            
        } catch (error) {
            trackEvent('cvr_lookup', {
                cvr_number: formData.cvr,
                status: 'error',
                error_message: error.message
            });
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async (confirmData) => {
        setLoading(true);
        setError(null);
        
        try {
            // Create company and get URL
            const { url } = await createCompany({
                ...companyPreview,
                companyEmail: confirmData.companyEmail,
                password: confirmData.password,
                existingWebsite: confirmData.existingWebsite
            });

            // Redirect to the new subdomain
            window.location.href = url;
            
        } catch (error) {
            console.error('Confirmation error:', error);
            setError(error.message);
            setLoading(false);
        }
    };

    return (
        <MainLayout>
            {/* Hero section with brand-dark background */}
            <div className="relative bg-brand-dark">
                <div className="absolute inset-0 bg-brand-dark/70"></div>
                <div className="relative max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-2xl sm:text-4xl font-bold font-montserrat text-white mb-4">
                            Opret din professionelle hjemmeside
                        </h1>
                        <p className="text-lg sm:text-xl text-white mb-4 max-w-md mx-auto">
                            Indtast dit CVR-nummer nedenfor, for at komme i gang med din nye hjemmeside.
                        </p>
                        <p className="text-lg sm:text-xl text-white mb-8 max-w-md mx-auto">
                            Det tager kun få minutter og du får de første 3 måneder 100% gratis, intet krav om betalingskort.
                        </p>
                    </div>
                </div>
            </div>

            {/* Form section */}
            <div className="max-w-2xl mx-auto -mt-8 px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="bg-white shadow-xl rounded-lg p-8">
                    {companyPreview ? (
                        <CompanyPreview
                            company={companyPreview}
                            onConfirm={handleConfirm}
                            onEdit={() => setCompanyPreview(null)}
                            loading={loading}
                        />
                    ) : (
                        <>
                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-semibold font-montserrat text-gray-900 mb-2">
                                    Start med dit CVR-nummer
                                </h2>
                                <p className="text-gray-600">
                                    Systemet finder automatisk dine virksomhedsoplysninger
                                </p>
                            </div>
                            <CvrForm
                                cvrNumber={cvrNumber}
                                setCvrNumber={setCvrNumber}
                                onSubmit={handleSubmit}
                                loading={loading}
                                error={error}
                            />
                        </>
                    )}
                </div>

                {/* Features preview */}
                <div className="mt-12 grid md:grid-cols-3 gap-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                        <div className="w-12 h-12 bg-brand-light/20 rounded-lg flex items-center justify-center mb-4">
                            <svg className="w-6 h-6 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold font-montserrat mb-2">Hurtig opsætning</h3>
                        <p className="text-gray-600 text-sm">Få din hjemmeside online på under 5 minutter</p>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                        <div className="w-12 h-12 bg-brand-light/20 rounded-lg flex items-center justify-center mb-4">
                            <svg className="w-6 h-6 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold font-montserrat mb-2">AI-genereret indhold</h3>
                        <p className="text-gray-600 text-sm">Professionelt indhold tilpasset din virksomhed</p>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                        <div className="w-12 h-12 bg-brand-light/20 rounded-lg flex items-center justify-center mb-4">
                            <svg className="w-6 h-6 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold font-montserrat mb-2">Nem at administrere</h3>
                        <p className="text-gray-600 text-sm">Opdater nemt indhold og billeder selv</p>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default Onboarding;